import React from "react";
import { Pill } from "../Ui";
import RuntimeCounter from "../RuntimeCounter";
import { UserRound, AlertTriangle } from "lucide-react";

export function RuntimeStatusBadge({ status }) {
  if (status === "RUNNING") {
    return <Pill tone="sage">🟢 RUNNING</Pill>;
  }
  if (status === "BREAKDOWN") {
    return <Pill tone="danger">⚠ BREAKDOWN</Pill>;
  }
  return <Pill tone="default">🔴 STOPPED</Pill>;
}

export function RuntimeActorLine({ label, name, role }) {
  if (!name) return null;
  return (
    <div className="flex items-center gap-2 text-sm text-foreground/80">
      <UserRound className="h-4 w-4 shrink-0 text-primary" />
      <span className="truncate">
        {label}: {name} · {role || "—"}
      </span>
    </div>
  );
}

export function RuntimeSectionHeader({ eyebrow, title, status }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {eyebrow && (
          <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground font-medium">{eyebrow}</div>
        )}
        <h3 className="font-serif text-lg sm:text-xl text-foreground leading-tight mt-1">{title}</h3>
      </div>
      <RuntimeStatusBadge status={status} />
    </div>
  );
}

export function RuntimeBreakdownBanner({ reason }) {
  if (!reason) return null;
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 flex items-start gap-2 text-sm text-red-900">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
      <div>
        <div className="font-medium">Breakdown reason</div>
        <div className="mt-0.5">{reason}</div>
      </div>
    </div>
  );
}

export function RuntimeUptimeBlock({ section, testIdPrefix }) {
  const running = section.status === "RUNNING";
  return (
    <RuntimeCounter
      startTime={section.start_time}
      running={running}
      testId={`${testIdPrefix}-counter`}
      stoppedLabel={section.status === "BREAKDOWN" ? "Breakdown — timer stopped" : "Stopped"}
    />
  );
}

export function RuntimeLogHistory({ logs, emptyMessage = "No runtime logs yet." }) {
  if (!logs?.length) {
    return <p className="text-sm text-muted-foreground py-6 text-center">{emptyMessage}</p>;
  }
  return (
    <div className="space-y-2 max-h-72 overflow-y-auto">
      {logs.map((log) => (
        <div
          key={log.id}
          className="rounded-lg border border-border/70 bg-muted/30 px-3 py-2.5 text-xs sm:text-sm"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{log.section_name}</span>
            <RuntimeStatusBadge status={log.status} />
            <span className="font-mono tabular-nums text-primary">
              {log.duration_seconds != null ? formatDuration(log.duration_seconds) : "—"}
            </span>
          </div>
          <div className="mt-1.5 text-muted-foreground space-y-0.5">
            <div>Started by {log.started_by_name || "—"}</div>
            {log.stopped_by_name && <div>Stopped by {log.stopped_by_name}</div>}
            {log.breakdown_reason && <div className="text-red-800">Breakdown: {log.breakdown_reason}</div>}
            {log.remarks && !log.breakdown_reason && <div>Note: {log.remarks}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function formatDuration(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
